/**
 * Notification Dropdown Component
 * Displays list of notifications in a dropdown
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import type { Notification } from "../../store/api/notificationsApi";
import { useMarkNotificationReadMutation } from "../../store/api/notificationsApi";
import { formatDateTime } from "../../utils/formatDate";
import Icon from "../AppIcon";
import Button from "../ui/Button";

interface NotificationDropdownProps {
	notifications: Notification[];
	onMarkAllRead: () => void;
	onClose: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
	notifications,
	onMarkAllRead,
	onClose,
}) => {
	const navigate = useNavigate();
	const [markRead] = useMarkNotificationReadMutation();

	const unreadCount = notifications.filter((n) => !n.is_read).length;

	const handleNotificationClick = async (notification: Notification) => {
		// Mark as read when clicked (even if already read, to ensure it's marked)
		if (!notification.is_read) {
			try {
				await markRead(notification.id).unwrap();
			} catch (error) {
				console.error("Failed to mark notification as read:", error);
			}
		}

		// Navigate if there's a link (e.g., property requests)
		if (notification.link) {
			onClose(); // Close dropdown first
			// Extract request_id from query params if present
			if (notification.link.includes("property-requests-management")) {
				const url = new URL(notification.link, window.location.origin);
				const requestId = url.searchParams.get("request_id");
				if (requestId) {
					navigate(`/property-requests-management?request_id=${requestId}`);
				} else {
					navigate("/property-requests-management");
				}
			} else {
				navigate(notification.link);
			}
		}
	};

	const getNotificationIcon = (type: Notification["type"]) => {
		switch (type) {
			case "payment_due":
			case "payment_received":
				return "DollarSign";
			case "maintenance_request":
			case "maintenance_completed":
				return "Wrench";
			case "lease_expiring":
				return "Calendar";
			case "message":
				return "MessageSquare";
			case "system":
				return "Info";
			default:
				return "Bell";
		}
	};

	const getNotificationColor = (type: Notification["type"]) => {
		switch (type) {
			case "payment_due":
			case "maintenance_request":
				return "text-warning";
			case "payment_received":
			case "maintenance_completed":
				return "text-success";
			case "lease_expiring":
				return "text-destructive";
			default:
				return "text-primary";
		}
	};

	return (
		<div className="absolute right-0 mt-2 w-80 md:w-96 bg-card border border-border rounded-lg shadow-lg z-[90] max-h-[500px] flex flex-col">
			{/* Header */}
			<div className="flex items-center justify-between p-4 border-b border-border">
				<h3 className="text-lg font-semibold text-foreground">Notifications</h3>
				{unreadCount > 0 && (
					<Button
						variant="ghost"
						size="sm"
						onClick={onMarkAllRead}
						className="text-xs"
					>
						Read
					</Button>
				)}
			</div>

			{/* Notifications List */}
			<div className="flex-1 overflow-y-auto">
				{notifications.length === 0 ? (
					<div className="p-8 text-center">
						<Icon
							name="Bell"
							size={32}
							className="mx-auto mb-3 text-muted-foreground"
						/>
						<p className="text-sm text-muted-foreground">No notifications</p>
					</div>
				) : (
					<div className="divide-y divide-border">
						{notifications.map((notification) => (
							<button
								key={notification.id}
								onClick={() => handleNotificationClick(notification)}
								className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
									!notification.is_read ? "bg-muted/30" : ""
								}`}
							>
								<div className="flex items-start gap-3">
									{/* Icon */}
									<div
										className={`flex-shrink-0 mt-0.5 ${getNotificationColor(
											notification.type,
										)}`}
									>
										<Icon
											name={getNotificationIcon(notification.type)}
											size={20}
										/>
									</div>

									{/* Content */}
									<div className="flex-1 min-w-0">
										<div className="flex items-start justify-between gap-2">
											<h4
												className={`text-sm font-medium text-foreground ${
													!notification.is_read ? "font-semibold" : ""
												}`}
											>
												{notification.title}
											</h4>
											{!notification.is_read && (
												<div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary" />
											)}
										</div>
										<p className="text-sm text-muted-foreground mt-1 line-clamp-2">
											{notification.message}
										</p>
										<p className="text-xs text-muted-foreground mt-2">
											{formatDateTime(notification.created_at)}
										</p>
									</div>
								</div>
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default NotificationDropdown;
